import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ModalContainer,
  PageContainer,
  Sidebar,
  MainContent,
  SidebarItem,
  SidebarIconContainer,
  Title,
  CloseButton,
  ButtonGroup,
  Button,
} from './NewGame.styles';
import Step1 from './steps/Step1';
import Step2 from './steps/Step2';
import Step3 from './steps/Step3';
import NewGameState from './_types/NewGameState';
import ErrorMessagesState from './_types/ErrorMessagesState';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { GamifyIcon } from 'application/components/GamifyIcon';
import CircularProgress from '@mui/material/CircularProgress';
import { GamifyToast } from 'application/common/CustomToasts';
import LoadingSkeleton from './components/LoadingSkeleton';
import LoadingState from './components/LoadingState';
import ConfirmationModal from 'application/routes/users/ConfirmationModal';
import { useTheme } from '@mui/material/styles';

interface Props {
  steps: string[];
  activeStep: number;
  onChangeActiveStep: (
    newActiveStep: number,
    customState?: any
  ) => Promise<boolean>;
  newGameState: NewGameState;
  onChangeNewGameState: (newGameState: NewGameState) => void;
  onSaveGame: () => Promise<void>;
  loading: boolean;
  error: any;
  shouldDisableNextOrFinishButton: boolean;
  setShouldDisableNextOrFinishButton: any;
  errorMessages: ErrorMessagesState;
  onClose: () => void;
  isEditMode?: boolean;
}

const SIDEBAR_ITEMS = [
  { icon: 'info', label: 'About', step: 0 },
  { icon: 'rules', label: 'Rules & Rewards', step: 1 },
  { icon: 'users', label: 'Contestants', step: 2 },
];

const NewGameView: React.FC<Props> = ({
  activeStep,
  steps,
  onChangeActiveStep,
  newGameState,
  onChangeNewGameState,
  onSaveGame,
  loading,
  shouldDisableNextOrFinishButton,
  setShouldDisableNextOrFinishButton,
  errorMessages,
  onClose,
  isEditMode = false,
}) => {
  const theme = useTheme();
  const [isSaving, setIsSaving] = useState(false);
  const [initialState] = useState<NewGameState>(newGameState);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const mainContentRef = useRef<HTMLDivElement>(null);

  // Function to scroll to top of main content
  const scrollToTop = useCallback(() => {
    if (mainContentRef.current) {
      mainContentRef.current.scrollTop = 0;
    }
  }, []);

  // Helper function to get confirmation message
  const getConfirmationMessage = useCallback(() => {
    return `Are you sure you wish to cancel the game ${
      isEditMode ? 'editing' : 'creation'
    } process? Any unsaved changes will be lost.`;
  }, [isEditMode]);

  // Check if user has made changes that would be lost on close
  const hasChanges = useCallback(() => {
    // For new games, check if user has entered basic info
    if (!isEditMode) {
      return (
        !!newGameState.name ||
        !!newGameState.description ||
        !!newGameState.KPIRuleID ||
        !!newGameState.assigneeIDs?.length
      );
    }

    // For edit mode, check if there are differences from initial state
    const hasNameChanged = initialState.name !== newGameState.name;
    const hasDescriptionChanged =
      initialState.description !== newGameState.description;
    const hasRuleChanged = initialState.KPIRuleID !== newGameState.KPIRuleID;

    // Check if contestants changed
    const hasContestantsChanged =
      JSON.stringify(initialState.assigneeIDs || []) !==
      JSON.stringify(newGameState.assigneeIDs || []);

    return (
      hasNameChanged ||
      hasDescriptionChanged ||
      hasRuleChanged ||
      hasContestantsChanged
    );
  }, [initialState, newGameState, isEditMode]);

  // Ensure we're just closing without showing any success message
  const handleConfirmClose = useCallback(() => {
    // Just close the modal directly - no success message
    setShowConfirmation(false);

    onClose();
  }, [onClose]);

  // Create handleClose as a memoized callback so we can use it in useEffect
  const handleClose = useCallback(() => {
    if (isSaving) return;

    // Skip confirmation if no changes
    if (!hasChanges()) {
      onClose();
      return;
    }

    // Show confirmation dialog
    setShowConfirmation(true);
  }, [hasChanges, isSaving, onClose]);

  // Handle browser close/reload events
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Only prompt if there are changes
      if (hasChanges()) {
        const message = getConfirmationMessage();
        e.preventDefault();
        e.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasChanges, getConfirmationMessage]);

  // Handle escape key press
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleClose();
      }
    };

    // Only add the listener if we're not currently saving
    if (!isSaving) {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
    return undefined;
  }, [handleClose, isSaving]);

  const handlePrevious = async () => {
    if (isSaving) return;

    if (activeStep === 0) {
      // If we're on the first step, check for changes before showing confirmation
      if (!hasChanges()) {
        onClose();
        return;
      }

      // Show cancel confirmation
      setShowConfirmation(true);
    } else {
      // If we're on a later step, go to the previous step
      await onChangeActiveStep(activeStep - 1);
    }
  };

  const handleNext = async () => {
    if (shouldDisableNextOrFinishButton || isSaving) return;

    if (activeStep === steps.length - 1) {
      // Final step validation - skip assignee check for bracket games
      if (
        activeStep === 2 &&
        !newGameState.contestType?.includes('bracket') &&
        (!newGameState.assigneeIDs || newGameState.assigneeIDs.length === 0)
      ) {
        GamifyToast.error(
          'Required fields',
          'Please add at least one contestant before proceeding'
        );
        return;
      }

      setIsSaving(true);
      try {
        await onSaveGame();
      } catch (error) {
        // Error toast is already shown by the hook/container layer — no duplicate toast here
      } finally {
        setIsSaving(false);
      }
      return;
    }

    // Simple validation check - skip for bracket games as they have more complex validation
    if (
      (activeStep === 0 &&
        (!newGameState.name || newGameState.name.length < 3)) ||
      (activeStep === 1 &&
        !newGameState.contestType?.includes('bracket') &&
        newGameState.contestType !== 'incentive' &&
        !newGameState.KPIRuleID)
    ) {
      GamifyToast.error(
        'Required fields',
        'Please fill in the required fields before proceeding'
      );
      return;
    }

    const navigated = await onChangeActiveStep(activeStep + 1);
    if (navigated === false) {
      GamifyToast.error(
        'Required fields',
        'Please fill in the required fields before proceeding'
      );
    }
  };

  const renderStepContent = () => {
    if (loading) return <LoadingSkeleton />;

    switch (activeStep) {
      case 0:
        return (
          <Step1
            newGameState={newGameState}
            onChangeNewGameState={onChangeNewGameState}
            errorMessages={errorMessages}
          />
        );
      case 1:
        return (
          <Step2
            newGameState={newGameState}
            onChangeNewGameState={onChangeNewGameState}
            setShouldDisableNextOrFinishButton={
              setShouldDisableNextOrFinishButton
            }
            scrollToTop={scrollToTop}
          />
        );
      case 2:
        return (
          <Step3
            newGameState={newGameState}
            onChangeNewGameState={onChangeNewGameState}
          />
        );
      default:
        return null;
    }
  };

  // Combined loading state from props and local saving state
  const isLoading = loading || isSaving;

  return (
    <ModalContainer theme={theme as any}>
      <Title theme={theme as any}>
        {isEditMode ? 'Edit Game' : 'Create New Game'}
      </Title>
      <CloseButton
        theme={theme as any}
        onClick={handleClose}
        disabled={isLoading}
      >
        <CloseIcon sx={{ fontSize: 24 }} />
      </CloseButton>

      {loading && isEditMode ? (
        <LoadingState />
      ) : (
        <>
          <PageContainer>
            <Sidebar theme={theme as any}>
              {SIDEBAR_ITEMS.map((item, index) => (
                <SidebarItem
                  key={item.label}
                  theme={theme as any}
                  active={activeStep === index}
                  onClick={async () => {
                    if (!isLoading) {
                      // Only validate if trying to go to a later step
                      if (index > activeStep) {
                        // Simple validation for direct toast
                        if (
                          (activeStep === 0 &&
                            (!newGameState.name ||
                              newGameState.name.length < 3)) ||
                          (activeStep === 1 &&
                            newGameState.contestType !== 'incentive' &&
                            !newGameState.KPIRuleID) ||
                          (activeStep === 2 &&
                            (!newGameState.assigneeIDs ||
                              newGameState.assigneeIDs.length === 0))
                        ) {
                          GamifyToast.error(
                            'Required fields',
                            'Please fill in the required fields before proceeding'
                          );
                          return;
                        }
                      }

                      await onChangeActiveStep(index);
                    }
                  }}
                >
                  <SidebarIconContainer>
                    <GamifyIcon
                      icon={item.icon}
                      color={
                        activeStep === index
                          ? theme.custom?.primary || '#FF6A00'
                          : theme.custom?.textLight || '#6F878F'
                      }
                      width={20}
                      height={20}
                    />
                  </SidebarIconContainer>
                  {item.label}
                </SidebarItem>
              ))}
            </Sidebar>

            <MainContent theme={theme as any} ref={mainContentRef}>
              {renderStepContent()}
            </MainContent>
          </PageContainer>

          <ButtonGroup theme={theme as any}>
            {activeStep > 0 && (
              <Button
                theme={theme as any}
                onClick={handlePrevious}
                disabled={isLoading}
              >
                <ChevronLeftIcon sx={{ fontSize: 18 }} />
                <span>Previous</span>
              </Button>
            )}
            <Button
              theme={theme as any}
              variant="primary"
              onClick={handleNext}
              disabled={shouldDisableNextOrFinishButton || isLoading}
            >
              {isLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <>
                  {activeStep === steps.length - 1 ? (
                    <span>Save</span>
                  ) : (
                    <>
                      <span>Next</span>
                      <ChevronRightIcon sx={{ fontSize: 18 }} />
                    </>
                  )}
                </>
              )}
            </Button>
          </ButtonGroup>
        </>
      )}

      <ConfirmationModal
        open={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        title="Cancel Game"
        message={getConfirmationMessage()}
        confirmationButtonText="Yes, Cancel"
        onConfirm={handleConfirmClose}
        isLoading={false}
      />
    </ModalContainer>
  );
};

export default NewGameView;
